import { ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useGetDocumentPresignedUrl } from '../../hooks/useDceAnalysis';

interface Props {
  documentId: number;
  filename?: string;
  pageNumber: number | null;
  excerpt: string;
}

/**
 * Sprint 4.D — Source tracking Option A (cf. plan §4.4 + décision #10).
 *
 * Affichage compact : extrait textuel + page + bouton "Ouvrir le document".
 * Le bouton déclenche un appel `POST /tm/documents/:id/presigned-url` qui
 * retourne une URL fraîche valide 15 min, ouverte dans un nouvel onglet.
 * Aucune URL n'est mise en cache côté front.
 */
export function SourceLink({ documentId, filename, pageNumber, excerpt }: Props) {
  const presignMutation = useGetDocumentPresignedUrl();

  const handleOpen = async () => {
    try {
      const { url } = await presignMutation.mutateAsync(documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error("Impossible d'ouvrir le document source");
    }
  };

  return (
    <div className="mt-2 rounded-md border bg-muted/40 px-3 py-2 text-xs">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="font-medium truncate">
          {filename ?? `Doc #${documentId}`}
          {pageNumber !== null && (
            <span className="ml-1.5 inline-block rounded bg-background border px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
              p. {pageNumber}
            </span>
          )}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={handleOpen}
          disabled={presignMutation.isPending}
        >
          {presignMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <ExternalLink className="h-3 w-3 mr-1" />
              Ouvrir
            </>
          )}
        </Button>
      </div>
      <p className="text-muted-foreground line-clamp-3 whitespace-pre-line">
        {excerpt}
      </p>
    </div>
  );
}
